// client/src/components/leads/b2c/CustomerList.js
// PROFESSIONAL VERSION - Clean table, name only, no notes

import React from 'react';

export default function CustomerList({ customers, onViewCustomer }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  };

  const getSourceIcon = (source) => {
    const icons = {
      instagram: 'fab fa-instagram',
      facebook: 'fab fa-facebook',
      linkedin: 'fab fa-linkedin',
      whatsapp: 'fab fa-whatsapp',
      referral: 'fas fa-user-friends',
      website: 'fas fa-globe',
      email: 'fas fa-envelope',
      phone: 'fas fa-phone',
      walkin: 'fas fa-door-open',
      other: 'fas fa-ellipsis-h'
    };
    return icons[source?.toLowerCase()] || icons.other;
  };

  const getSourceColor = (source) => {
    const colors = {
      instagram: 'bg-purple-100 text-purple-600',
      facebook: 'bg-blue-100 text-blue-600',
      linkedin: 'bg-blue-100 text-blue-700',
      whatsapp: 'bg-green-100 text-green-600',
      referral: 'bg-green-100 text-green-600',
      website: 'bg-gray-100 text-gray-600',
      email: 'bg-yellow-100 text-yellow-700',
      phone: 'bg-indigo-100 text-indigo-600',
      walkin: 'bg-orange-100 text-orange-600',
      other: 'bg-gray-100 text-gray-600'
    };
    return colors[source?.toLowerCase()] || colors.other;
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-300">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Added
              </th>
              <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {customers.map((customer) => (
              <tr
                key={customer.id}
                onClick={() => onViewCustomer(customer)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
                      <span className="text-blue-600 font-bold text-sm">
                        {customer.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {customer.name}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm space-y-1">
                    {customer.phone && (
                      <div className="flex items-center text-gray-900">
                        <i className="fas fa-phone text-gray-400 mr-2 text-xs w-4"></i>
                        <span className="truncate">{customer.phone}</span>
                      </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center text-gray-600">
                        <i className="fas fa-envelope text-gray-400 mr-2 text-xs w-4"></i>
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                    {!customer.phone && !customer.email && (
                      <span className="text-xs text-gray-400">No contact info</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSourceColor(customer.source)}`}>
                    <i className={`${getSourceIcon(customer.source)} mr-1.5`}></i>
                    <span className="capitalize">
                      {customer.source?.charAt(0)?.toUpperCase() + customer.source?.slice(1) || 'Unknown'}
                    </span>
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-600">
                    {formatDate(customer.createdAt || customer.created_at)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewCustomer(customer);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center"
                  >
                    View Details
                    <i className="fas fa-chevron-right ml-2 text-xs"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200">
        {customers.map((customer) => (
          <div
            key={customer.id}
            onClick={() => onViewCustomer(customer)}
            className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3">
                  <span className="text-blue-600 font-bold">
                    {customer.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{customer.name}</p>
                  <p className="text-xs text-gray-500">{formatDate(customer.createdAt || customer.created_at)}</p>
                </div>
              </div>
              <span className={`flex-shrink-0 ml-2 px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(customer.source)}`}>
                <i className={`${getSourceIcon(customer.source)}`}></i>
              </span>
            </div>
            
            {(customer.phone || customer.email) && (
              <div className="ml-15 space-y-1">
                {customer.phone && (
                  <div className="flex items-center text-xs text-gray-600">
                    <i className="fas fa-phone text-gray-400 mr-2 w-4"></i>
                    <span className="truncate">{customer.phone}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center text-xs text-gray-600">
                    <i className="fas fa-envelope text-gray-400 mr-2 w-4"></i>
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}